import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Paths } from "../common/constants";
import MainContainer from "../components/mainContainer";
import { useIsLoggedIn } from "../hooks/useIsLoggedIn";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

interface PrivateRouteProps {
  children: JSX.Element;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const isLoggedIn = useIsLoggedIn();
  const location = useLocation();
  const { inProgress } = useMsal();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Wait for MSAL to finish processing before checking auth status
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        setIsCheckingAuth(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsCheckingAuth(true);
    }
  }, [inProgress]);

  if (location.pathname.includes("auth/refresh")) {
    return children;
  }

  // Check if Microsoft logout is in progress - don't redirect to login during logout
  const isMsalLogoutInProgress = sessionStorage.getItem("msal_logout_in_progress") === "true";
  if (isMsalLogoutInProgress) {
    // Clear the flag and return null (will redirect via MSAL)
    sessionStorage.removeItem("msal_logout_in_progress");
    return null;
  }

  // Show loading state while checking authentication
  if (isCheckingAuth && inProgress !== InteractionStatus.None) {
    return null; 
  }

  return isLoggedIn ? (
    <MainContainer>{children}</MainContainer>
  ) : (
    <Navigate to={Paths.LOGIN} />
  );
};

export default PrivateRoute;
